import {
  type Auth,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithCredential,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from 'firebase/auth';

/** Israeli mobile in E.164: +972 + 9 digits starting with 5. */
const IL_MOBILE_NATIONAL = /^5\d{8}$/;

/**
 * Normalises common Israeli phone inputs to E.164 (+9725XXXXXXXX).
 * Accepts 05X…, 5X…, 972…, +972….
 */
export function normalizeIsraeliMobileToE164(input: string): string {
  const raw = input.replace(/[\s\-().]/g, '');
  if (!raw) throw new Error('EMPTY_PHONE');

  let digits = raw;
  if (digits.startsWith('+')) digits = digits.slice(1);

  if (digits.startsWith('972')) {
    digits = digits.slice(3);
  }

  if (digits.startsWith('0')) digits = digits.slice(1);

  if (!IL_MOBILE_NATIONAL.test(digits)) {
    throw new Error('INVALID_IL_PHONE');
  }

  return `+972${digits}`;
}

export function phoneAuthErrorMessage(code: string): string {
  switch (code) {
    case 'EMPTY_PHONE':
      return 'נא להזין מספר טלפון נייד.';
    case 'INVALID_IL_PHONE':
      return 'מספר לא תקין. הזינו נייד ישראלי (למשל 050-1234567).';
    case 'auth/invalid-phone-number':
      return 'מספר הטלפון אינו תקין לפי Firebase.';
    case 'auth/missing-phone-number':
      return 'חסר מספר טלפון.';
    case 'auth/quota-exceeded':
      return 'חרגנו ממכסת שליחת SMS. נסו שוב מאוחר יותר.';
    case 'auth/captcha-check-failed':
      return 'אימות האבטחה נכשל. סמנו את תיבת reCAPTCHA למעלה, בטלו חוסמי פרסומות, או רעננו את הדף.';
    case 'auth/invalid-verification-code':
      return 'קוד האימות שגוי. בדקו ונסו שוב.';
    case 'auth/code-expired':
      return 'פג תוקף הקוד. בקשו קוד חדש.';
    case 'auth/session-expired':
      return 'פגה ההזדהות. בקשו קוד חדש.';
    case 'auth/credential-already-in-use':
      return 'מספר זה כבר משויך לחשבון קיים. התחברו עם אותו מספר או פנו לתמיכה.';
    case 'auth/provider-already-linked':
      return 'מספר הטלפון כבר מקושר לחשבון זה.';
    case 'auth/too-many-requests':
      return 'יותר מדי ניסיונות. המתינו רגע ונסו שוב.';
    case 'auth/operation-not-allowed':
      return 'שיטת ההתחברות בטלפון לא מופעלת בפרויקט Firebase (או שחסר Blaze ל-SMS).';
    default:
      return `שגיאת התחברות (${code}). נסו שוב.`;
  }
}

export function formatUnknownPhoneAuthError(e: unknown): string {
  if (e && typeof e === 'object' && 'code' in e && typeof (e as { code: string }).code === 'string') {
    return phoneAuthErrorMessage((e as { code: string }).code);
  }
  if (e instanceof Error) return e.message;
  return phoneAuthErrorMessage('unknown');
}

/** Detaches reCAPTCHA from the DOM; call before creating another verifier on the same container. */
export function disposeRecaptchaVerifier(verifier: RecaptchaVerifier | null): void {
  if (!verifier) return;
  try {
    verifier.clear();
  } catch {
    /* noop */
  }
}

/**
 * Visible **compact** reCAPTCHA is far more reliable than invisible on web (Safari,
 * React StrictMode double-mount, ad blockers, referrer / domain edge cases).
 */
export function createPhoneRecaptchaVerifier(
  auth: Auth,
  container: HTMLElement,
): RecaptchaVerifier {
  return new RecaptchaVerifier(auth, container, {
    size: 'compact',
    callback: () => {
      /* optional */
    },
  });
}

export async function requestPhoneSms(
  auth: Auth,
  e164Phone: string,
  verifier: RecaptchaVerifier,
): Promise<ConfirmationResult> {
  await verifier.render();
  return signInWithPhoneNumber(auth, e164Phone, verifier);
}

/** Completes phone verification (no anonymous session — always signs in with phone). */
export async function completePhoneVerification(
  auth: Auth,
  verificationId: string,
  code: string,
): Promise<void> {
  const credential = PhoneAuthProvider.credential(verificationId, code.trim());
  await signInWithCredential(auth, credential);
}
